import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Building2, User, CreditCard, FileText, Sparkles, Clock, Check, X, Copy, Loader2, CheckCircle2 } from 'lucide-react';

const SuperAdminSales = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [form, setForm] = useState({
    company_name: '',
    org_number: '',
    work_type: 'Bygg',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    account_type: 'paying',
    plan_template: 'pro',
    contract_months: 12,
    coupon: '',
    custom_features: {},
    employee_count: 3,
    demo_days: 7,
    billing_method: 'card',
    offer_trial: true,       // Fas 4 (#11): 14-dagars trial-toggle
    discount_percent: 0,     // Iter48: rabatt på alla framtida månadsfakturor
    send_agreement: true,
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.saListPlans().then((r) => {
      setPlans(r.data);
      const proPlan = r.data.find((p) => p.name.toLowerCase() === 'pro');
      if (proPlan) {
        setSelectedPlan(proPlan);
        setForm((f) => ({ ...f, plan_template: 'pro' }));
      }
    }).catch(() => toast.error('Kunde inte hämta paket'));
  }, []);

  const update = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const choosePlan = (p) => {
    setSelectedPlan(p);
    update('plan_template', p.name.toLowerCase());
    update('custom_features', {});
  };

  const toggleFeature = (key) => {
    const cur = { ...(form.custom_features || {}) };
    const base = selectedPlan?.features || {};
    const current = key in cur ? cur[key] : base[key];
    cur[key] = !current;
    update('custom_features', cur);
  };

  const isFeatureOn = (key) => {
    const cur = form.custom_features || {};
    if (key in cur) return cur[key];
    return selectedPlan?.features?.[key] ?? false;
  };

  const PLAN_PER_EMPLOYEE = { start: 199, pro: 149, business: 99 };
  const CONTRACT_OPTIONS = [
    { months: 6, label: '6 mån', multiplier: 1.10, badge: '+10 %', tone: 'red' },
    { months: 12, label: '12 mån', multiplier: 1.0, badge: 'Standard', tone: 'neutral' },
    { months: 24, label: '24 mån', multiplier: 0.85, badge: '−15 %', tone: 'green' },
  ];

  const contractMultiplier = useMemo(() => {
    const o = CONTRACT_OPTIONS.find((c) => c.months === form.contract_months);
    return o ? o.multiplier : 1.0;
  }, [form.contract_months]);

  const computedMonthlyPrice = useMemo(() => {
    if (!selectedPlan) return 0;
    const key = selectedPlan.name.toLowerCase();
    const perEmp = PLAN_PER_EMPLOYEE[key] || 0;
    const extras = Math.max(0, (form.employee_count || 1) - 1);
    // Multiplier appliceras endast på baspaketet — extra användare har ingen bindning
    const base = Math.round(selectedPlan.monthly_price * contractMultiplier);
    return base + extras * perEmp;
  }, [selectedPlan, form.employee_count, contractMultiplier]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.admin_name || !form.admin_email) {
      toast.error('Fyll i företag, namn och e-post');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.saCreateCustomer(form);
      setResult(res.data);
      toast.success('Kund skapad!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa kund');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopierat');
  };

  const reset = () => {
    setResult(null);
    setForm((f) => ({ ...f, company_name: '', org_number: '', admin_name: '', admin_email: '', admin_phone: '' }));
  };

  const FEATURE_LABELS = {
    invoicing: 'Fakturering',
    payroll: 'Löner',
    bookkeeping: 'AI-bokföring',
    material_ai: 'AI Material',
    eta: 'ÄTA',
    time_reports: 'Tidrapporter',
    machine_hours: 'Maskintimmar',
    daybook: 'Dagböcker',
    tenders: 'Anbud (AI)',
    compare_offers: 'Jämför offerter',
    support_chat: 'Support-chatt',
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="sales-result">
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-2xl p-7">
          <CheckCircle2 size={36} className="text-green-600 mb-3" />
          <h2 className="text-2xl font-bold">Kund skapad!</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {form.company_name} har fått välkomstmail med inloggning + (om paying) avtals­länk via mail.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="bg-card border border-border rounded p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Initialt lösenord</p>
              <div className="flex gap-2 items-center mt-2">
                <code className="font-mono text-sm bg-muted px-2 py-1 rounded">{result.initial_password}</code>
                <Button variant="outline" size="sm" onClick={() => copy(result.initial_password)}><Copy size={14} /></Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Användaren tvingas byta vid första login. Spara EJ — skicka muntligt om kund inte fått mailet.
              </p>
            </div>
            <div className="bg-card border border-border rounded p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Plan</p>
              <p className="text-base font-bold mt-2">{result.plan_label} — {result.monthly_price.toLocaleString('sv-SE')} kr/mån <span className="text-xs font-normal text-muted-foreground">exkl. moms</span></p>
              {result.demo_expires_at && (
                <p className="text-xs text-amber-700 mt-1">Demo går ut: {new Date(result.demo_expires_at).toLocaleDateString('sv-SE')}</p>
              )}
            </div>
            {result.agreement_signing_url && (
              <div className="bg-card border border-border rounded p-4 sm:col-span-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Avtals­länk (BankID)</p>
                <div className="flex gap-2 items-center mt-2">
                  <Input value={result.agreement_signing_url} readOnly className="text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copy(result.agreement_signing_url)}><Copy size={14} /></Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Mailad till {form.admin_email}. När de signerar startar Stripe-trial (14 dagar gratis) automatiskt.
                </p>
              </div>
            )}
          </div>
          <Button onClick={reset} className="mt-6 bg-[#E8613A] hover:bg-[#D4532F] text-white">Skapa nästa kund</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="sales-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles size={22} className="text-[#E8613A]" /> Skapa ny kund
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Försäljnings­verktyg — skapa konto, välj paket, skicka avtal, starta provperiod.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Företag + admin */}
        <section className="bg-card border border-border rounded-xl p-5">
          <h2 className="font-bold flex items-center gap-2 mb-4"><Building2 size={18} /> Företag &amp; ansvarig</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs">Företagsnamn *</Label><Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="Bygg AB" className="mt-1.5" data-testid="sa-company-name" required /></div>
            <div><Label className="text-xs">Org.nummer</Label><Input value={form.org_number} onChange={(e) => update('org_number', e.target.value)} placeholder="556677-1234" className="mt-1.5" /></div>
            <div><Label className="text-xs">Bransch</Label><Input value={form.work_type} onChange={(e) => update('work_type', e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs">Antal anställda</Label><Input type="number" value={form.employee_count} onChange={(e) => update('employee_count', parseInt(e.target.value) || 1)} className="mt-1.5" /></div>
            <div><Label className="text-xs">Ansvarig namn *</Label><Input value={form.admin_name} onChange={(e) => update('admin_name', e.target.value)} placeholder="Förnamn Efternamn" className="mt-1.5" data-testid="sa-admin-name" required /></div>
            <div><Label className="text-xs">Ansvarig e-post *</Label><Input type="email" value={form.admin_email} onChange={(e) => update('admin_email', e.target.value)} placeholder="anna@foretag.se" className="mt-1.5" data-testid="sa-admin-email" required /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Telefon</Label><Input value={form.admin_phone} onChange={(e) => update('admin_phone', e.target.value)} placeholder="070-123 45 67" className="mt-1.5" /></div>
          </div>
        </section>

        {/* Konto-typ */}
        <section className="bg-card border border-border rounded-xl p-5">
          <h2 className="font-bold flex items-center gap-2 mb-4"><Clock size={18} /> Konto-typ</h2>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => update('account_type', 'paying')} className={`p-4 rounded-lg border-2 text-left transition-all ${form.account_type === 'paying' ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border hover:border-[#E8613A]/40'}`} data-testid="sa-paying-btn">
              <p className="font-bold">Betalande kund</p>
              <p className="text-xs text-muted-foreground mt-1">Avtal + 14 dagars trial → fakturering startar</p>
            </button>
            <button type="button" onClick={() => update('account_type', 'demo')} className={`p-4 rounded-lg border-2 text-left transition-all ${form.account_type === 'demo' ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border hover:border-[#E8613A]/40'}`} data-testid="sa-demo-btn">
              <p className="font-bold">Demo-konto</p>
              <p className="text-xs text-muted-foreground mt-1">Tidsbegränsad, ingen fakturering</p>
            </button>
          </div>
          {form.account_type === 'demo' && (
            <div className="mt-4 max-w-xs">
              <Label className="text-xs">Demo-tid (dagar)</Label>
              <Input type="number" min="1" max="90" value={form.demo_days} onChange={(e) => update('demo_days', parseInt(e.target.value) || 7)} className="mt-1.5" data-testid="sa-demo-days" />
              <p className="text-xs text-muted-foreground mt-1">Login blockeras automatiskt efter den här tiden.</p>
            </div>
          )}
        </section>

        {/* Paket */}
        <section className="bg-card border border-border rounded-xl p-5">
          <h2 className="font-bold flex items-center gap-2 mb-4"><FileText size={18} /> Paket</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {plans.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => choosePlan(p)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${form.plan_template === p.name.toLowerCase() ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border hover:border-[#E8613A]/40'}`}
                data-testid={`sa-plan-${p.name.toLowerCase()}`}
              >
                <p className="font-bold">{p.name}</p>
                <p className="text-2xl font-bold mt-1">{p.monthly_price.toLocaleString('sv-SE')} kr<span className="text-xs text-muted-foreground">/mån</span></p>
                <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">exkl. 25 % moms</p>
                <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
              </button>
            ))}
          </div>

          {selectedPlan && (
            <div className="mt-5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Bindningstid</Label>
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="sa-contract-options">
                {CONTRACT_OPTIONS.map((opt) => {
                  const active = form.contract_months === opt.months;
                  const toneClass =
                    opt.tone === 'green' ? 'text-emerald-700 dark:text-emerald-400' :
                    opt.tone === 'red' ? 'text-rose-600 dark:text-rose-400' :
                    'text-muted-foreground';
                  return (
                    <button
                      key={opt.months}
                      type="button"
                      onClick={() => update('contract_months', opt.months)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border hover:border-[#E8613A]/40'}`}
                      data-testid={`sa-contract-${opt.months}`}
                    >
                      <p className="font-bold text-sm">{opt.label}</p>
                      <p className={`text-[11px] mt-0.5 font-medium ${toneClass}`}>{opt.badge}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedPlan && (
            <div className="mt-4 bg-[#E8613A]/5 border border-[#E8613A]/30 rounded-lg p-4" data-testid="sa-price-breakdown">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-[#E8613A] font-bold">Totalt månadspris</p>
                <p className="text-2xl font-bold" data-testid="sa-total-monthly">
                  {computedMonthlyPrice.toLocaleString('sv-SE')} <span className="text-xs text-muted-foreground font-normal">kr/mån exkl. moms</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Baspaket ({selectedPlan.name}, {form.contract_months} mån)</span>
                  <span className="font-medium text-foreground">
                    {Math.round(selectedPlan.monthly_price * contractMultiplier).toLocaleString('sv-SE')} kr
                    {contractMultiplier !== 1.0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({contractMultiplier > 1 ? '+' : '−'}{Math.abs(Math.round((contractMultiplier - 1) * 100))}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between"><span>{Math.max(0, (form.employee_count || 1) - 1)} × extra användare</span><span className="font-medium text-foreground">+{(Math.max(0, (form.employee_count || 1) - 1) * (PLAN_PER_EMPLOYEE[selectedPlan.name.toLowerCase()] || 0)).toLocaleString('sv-SE')} kr</span></div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
                1 användare ingår i baspaketet. Extra: {PLAN_PER_EMPLOYEE[selectedPlan.name.toLowerCase()] || 0} kr/mån. Bindning gäller endast baspaketet — extra användare har ingen bindning (1 månads uppsägning).
              </p>
            </div>
          )}

          {selectedPlan && (
            <details className="mt-5">
              <summary className="cursor-pointer text-sm font-medium text-[#E8613A]">Anpassa funktioner ({selectedPlan.name})</summary>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleFeature(key)}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm text-left ${isFeatureOn(key) ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border opacity-60'}`}
                    data-testid={`sa-feature-${key}`}
                  >
                    <span className="truncate">{label}</span>
                    {isFeatureOn(key) ? <Check size={14} className="text-[#E8613A] flex-shrink-0" /> : <X size={14} className="opacity-40 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </details>
          )}
        </section>

        {/* Betalning */}
        {form.account_type === 'paying' && (
          <section className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-bold flex items-center gap-2 mb-4"><CreditCard size={18} /> Betalningssätt</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { v: 'card', t: 'Kort / Autogiro', d: 'Stripe drar automatiskt varje månad.' },
                { v: 'invoice', t: 'Faktura', d: 'Stripe mailar PDF-faktura. 30 dagar netto.' },
                { v: 'none', t: 'Manuellt', d: 'Ingen Stripe — vi fakturerar utanför systemet.' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => update('billing_method', opt.v)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${form.billing_method === opt.v ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border'}`}
                  data-testid={`sa-billing-${opt.v}`}
                >
                  <p className="font-bold">{opt.t}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.d}</p>
                </button>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-border">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Rabattkod (valfri)</Label>
              <Input
                value={form.coupon}
                onChange={(e) => update('coupon', e.target.value.trim().toUpperCase())}
                placeholder="t.ex. BLACKFRIDAY25 eller LAUNCH50"
                className="mt-1.5"
                data-testid="sa-coupon-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Stripe Promotion Code eller Coupon ID. Lämnas tomt om kunden ska kunna skriva in själv i Stripe Checkout.
              </p>
            </div>
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border">
              <input type="checkbox" id="sa-send-agreement" checked={form.send_agreement} onChange={(e) => update('send_agreement', e.target.checked)} className="w-4 h-4" data-testid="sa-send-agreement" />
              <Label htmlFor="sa-send-agreement" className="text-sm cursor-pointer">Skicka Operio-tjänsteavtal via BankID-signering</Label>
            </div>

            {/* Fas 4 (#11): 14-dagars provperiod toggle */}
            <div className="mt-4 pt-4 border-t border-border">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.offer_trial}
                  onChange={(e) => update('offer_trial', e.target.checked)}
                  className="w-4 h-4 mt-0.5"
                  data-testid="sa-offer-trial"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold">Erbjud 14 dagars gratis provperiod</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    {form.offer_trial
                      ? 'Faktura skickas dag 15. Om obetald efter ytterligare 20 dagar → kontot låses automatiskt.'
                      : 'Faktura skickas direkt. Om obetald efter 30 dagar → kontot låses automatiskt.'}
                  </p>
                </div>
              </label>
            </div>

            {/* Iter48: Rabatt-fält som gäller alla framtida månadsfakturor */}
            <div className="mt-4 pt-4 border-t border-border">
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                Månatlig rabatt (%)
              </Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="number" min="0" max="100" step="1"
                  value={form.discount_percent}
                  onChange={(e) => update('discount_percent', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                  className="h-10 w-28 px-3 rounded-md border border-border bg-background text-sm"
                  data-testid="sa-discount-percent"
                />
                <p className="text-[11px] text-muted-foreground flex-1 leading-relaxed">
                  Appliceras som en negativ rad på VARJE månadsfaktura (paket + användare)
                  tills du nollställer värdet i företagslistan.
                </p>
              </div>
            </div>
          </section>
        )}

        <Button type="submit" disabled={submitting} className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 rounded-full text-base font-semibold" data-testid="sa-create-submit">
          {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Skapar kund…</> : 'Skapa kund + skicka mail'}
        </Button>
      </form>
    </div>
  );
};

export default SuperAdminSales;
